
import { defineComponent, PropType, nextTick, ref } from "vue";
import { AccountantItemResponse } from "@/core/interfaces/ApiResponses";
import { ElAutocomplete } from "element-plus";
import AccountantService from "@/core/services/Accountant.service";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";

const SENTINEL_LOADING = "Đang tìm kiếm";
const SENTINEL_EMPTY = "Không có kết quả";

type SuggestionItem = AccountantItemResponse & {
  value?: string;
  is_sentinel?: boolean;
};

export default defineComponent({
  name: "tai-khoan-selection-v2",

  components: {
    LabelCustom,
  },

  emits: [
    "on-select-ma-tai-khoan",
    "on-select-ma-chi-tiet",
    "on-select",
    "on-clear",
    "on-loading",
  ],

  data() {
    return {
      tenTaiKhoan: "",
      maTaiKhoanDangChon: "" as string | number,
      maChiTietDangChon: "",
      selectedPhanCap: null as number | null,
      maTaiKhoanBeforeSuggest: "" as string | number,
      maChiTietBeforeSuggest: "",
      /** Blocks prop hydrate from restoring chi tiết after a new mã TK search/select */
      suppressMactHydrate: false,
      /**
       * After user clicks clear, ignore stale parent props until they catch up
       * (or until a new selection). Prevents hydrate from restoring cleared
       * values and breaking subsequent search API calls.
       */
      suppressPropHydrateAfterClear: false,
      /** Which field the user last cleared — avoids mistaking sequential parent updates */
      lastClearedField: null as null | "tai_khoan" | "chi_tiet",
    };
  },

  props: {
    label: {
      type: String,
      default: "",
    },
    labelClasses: {
      type: String,
      default: "text-start",
    },
    id: {
      type: String,
      default: "tai-khoan-selection",
    },
    isBolder: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    isTextMuted: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    isRequired: {
      type: Boolean as PropType<boolean>,
      default: undefined,
    },
    orientation: {
      type: String as PropType<"horizontal" | "vertical">,
      default: "horizontal",
      validator: (value: string) => {
        return value === "horizontal" || value === "vertical";
      },
    },
    maTaiKhoanProp: {
      type: [Number, Array, String],
      default: 0,
    },
    maChiTietProp: {
      type: String,
      default: "",
    },
    tenTaiKhoanProp: {
      type: String,
      default: "",
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    autoSearch: {
      type: Boolean,
      default: true,
    },
    operator: {
      type: String as PropType<"like" | "in" | null>,
      default: null,
      validator: (value: string | null) => {
        return value === null || value === "like" || value === "in";
      },
    },
    disableMaTaiKhoanProp: {
      type: Boolean,
      default: false,
    },
    /** @deprecated Always hydrates from props without API. Kept for backward compatibility. */
    prefillWithoutApi: {
      type: Boolean,
      default: false,
    },
    startTabIndex: {
      type: Number,
      default: undefined,
    },
  },

  setup() {
    const maChiTietInputRef = ref<null | typeof ElAutocomplete>(null);
    const maTaiKhoanInputRef = ref<null | typeof ElAutocomplete>(null);

    return {
      maChiTietInputRef,
      maTaiKhoanInputRef,
    };
  },

  mounted() {
    this.applyInitialFromProps();
  },

  computed: {
    isRequiredComputed(): boolean {
      if (this.isRequired !== undefined) {
        return this.isRequired;
      }

      return !this.readOnly;
    },

    isInputTaiKhoanChiTietDisabled(): boolean {
      if (this.disableMaTaiKhoanProp && !this.hasProvidedMatk()) {
        return true;
      }

      return this.selectedPhanCap !== 0;
    },

    maChiTietPlaceholder(): string {
      if (this.readOnly) {
        return "";
      }

      // Selected mã TK that does not allow chi tiết — show dash instead of hint text
      const hasSelectedMatk = Boolean(this.maTaiKhoanDangChon);
      if (hasSelectedMatk && this.isInputTaiKhoanChiTietDisabled) {
        return "-";
      }

      return "Mã chi tiết";
    },
  },

  watch: {
    maChiTietProp() {
      this.applyInitialFromProps();
    },

    maTaiKhoanProp() {
      this.applyInitialFromProps();
    },

    tenTaiKhoanProp() {
      this.applyInitialFromProps();
    },

    disableMaTaiKhoanProp(value) {
      if (value && this.hasProvidedMatk()) {
        this.maTaiKhoanDangChon = String(this.maTaiKhoanProp);
      }
    },
  },

  methods: {
    hasProvidedMatk() {
      const value = this.maTaiKhoanProp;

      return (
        value !== 0 &&
        value !== "" &&
        value != null &&
        !(Array.isArray(value) && value.length === 0)
      );
    },

    makeSentinel(label: string): SuggestionItem {
      return {
        id: -1,
        ma_tai_khoan: "",
        ma_chi_tiet: "",
        ten_tai_khoan: label,
        phan_cap: -1,
        value: label,
        is_sentinel: true,
      } as SuggestionItem;
    },

    isSentinelItem(item: SuggestionItem | AccountantItemResponse) {
      return Boolean((item as SuggestionItem).is_sentinel);
    },

    normalizeTenTaiKhoanProp(value: string) {
      // Legacy clearTaiKhoanData used tenTaiKhoan = '0' as a sentinel
      if (!value || value === "0") {
        return "";
      }

      return value;
    },

    syncTenTaiKhoanFromProps(matk: string, tentk: string) {
      // Avoid blanking local name when parent omits tenTaiKhoanProp after select
      if (!matk) {
        this.tenTaiKhoan = tentk;
        return;
      }

      if (tentk) {
        this.tenTaiKhoan = tentk;
      }
    },

    applyInitialFromProps() {
      const matk = this.hasProvidedMatk() ? String(this.maTaiKhoanProp) : "";
      const mact = this.maChiTietProp || "";
      const tentk = this.normalizeTenTaiKhoanProp(this.tenTaiKhoanProp || "");

      // User cleared while parent still holds old props — do not restore them
      if (this.suppressPropHydrateAfterClear) {
        if (this.lastClearedField === "tai_khoan") {
          // Wait until parent cleared both codes (maChiTiet often updates first)
          if (!matk && !mact) {
            this.suppressPropHydrateAfterClear = false;
            this.lastClearedField = null;
            // Keep in-progress search text; only reset if input is still empty
            if (!String(this.maTaiKhoanDangChon || "").trim()) {
              this.maTaiKhoanDangChon = "";
              this.tenTaiKhoan = tentk;
            }
            this.maChiTietDangChon = "";
            this.selectedPhanCap = null;
            this.suppressMactHydrate = false;
          }

          return false;
        }

        if (this.lastClearedField === "chi_tiet") {
          if (!mact) {
            this.suppressPropHydrateAfterClear = false;
            this.lastClearedField = null;
            this.maTaiKhoanDangChon = matk;
            this.syncTenTaiKhoanFromProps(matk, tentk);
            this.maChiTietDangChon = "";
            return Boolean(matk || this.tenTaiKhoan);
          }

          return false;
        }

        // Parent still has stale values from before clear — ignore
        return false;
      }

      this.maTaiKhoanDangChon = matk;
      this.syncTenTaiKhoanFromProps(matk, tentk);

      if (!matk) {
        this.maChiTietDangChon = "";
        this.selectedPhanCap = null;
        this.suppressMactHydrate = false;
        return Boolean(mact || tentk);
      }

      // Account that does not allow chi tiết — never revive stale mact from props
      if (this.selectedPhanCap !== null && this.selectedPhanCap !== 0) {
        this.maChiTietDangChon = "";
        return Boolean(matk || this.tenTaiKhoan);
      }

      // After a new mã TK search/select, do not recover previous ma_chi_tiet from props
      if (this.suppressMactHydrate) {
        this.maChiTietDangChon = "";

        if (!mact) {
          this.suppressMactHydrate = false;
        }

        return Boolean(matk || this.tenTaiKhoan);
      }

      this.maChiTietDangChon = mact;

      if (mact) {
        this.selectedPhanCap = 0;
      }

      return Boolean(matk || mact || this.tenTaiKhoan);
    },

    clearChiTietForNewMatkSearch() {
      this.maChiTietDangChon = "";
      this.maChiTietBeforeSuggest = "";
      this.suppressMactHydrate = true;
      // Disable until a new mã TK is selected (enable only if phan_cap === 0)
      this.selectedPhanCap = null;
    },

    resetChiTietState() {
      this.maChiTietDangChon = "";
      this.selectedPhanCap = null;
      this.suppressMactHydrate = false;
      this.maChiTietBeforeSuggest = "";
    },

    focusInput(type: "tai_khoan" | "chi_tiet", options?: { forceReactivate?: boolean }) {
      nextTick(() => {
        const inputRef =
          type === "tai_khoan"
            ? this.maTaiKhoanInputRef
            : this.maChiTietInputRef;

        const nativeInput = document.getElementById(
          type === "tai_khoan" ? `${this.id}_tai-khoan` : `${this.id}_chi-tiet`
        ) as HTMLInputElement | null;

        if (!nativeInput || nativeInput.disabled) {
          return;
        }

        const activate = () => {
          inputRef?.focus?.();
          nativeInput.focus();
          nativeInput.select();
        };

        // EP handleClear sets activated=false. If the input never blurred,
        // a plain focus() will not re-fire handleFocus, so suggestions stay
        // hidden even when fetchSuggestions returns data. Blur → focus resets it.
        if (options?.forceReactivate) {
          nativeInput.blur();
          nextTick(activate);
          return;
        }

        activate();
      });
    },

    iconClickedHandler(type: "tai_khoan" | "chi_tiet") {
      // Block stale prop hydrate until parent clears via @on-clear.
      // Prevents restored props from wiping the input and breaking search API.
      this.suppressPropHydrateAfterClear = true;
      this.lastClearedField = type;

      if (type === "tai_khoan") {
        this.maTaiKhoanDangChon = "";
        this.tenTaiKhoan = "";
        this.resetChiTietState();
      } else {
        this.maChiTietDangChon = "";
        this.maChiTietBeforeSuggest = "";
      }

      this.$emit("on-clear", type);
      this.focusInput(type, { forceReactivate: true });
    },

    async queryTaiKhoan(queryString: string, cb) {
      if (!queryString || queryString.trim() === "") {
        cb([]);
        return;
      }

      this.maTaiKhoanBeforeSuggest = this.maTaiKhoanDangChon;
      this.clearChiTietForNewMatkSearch();
      cb([this.makeSentinel(SENTINEL_LOADING)]);
      this.$emit("on-loading", true);

      try {
        const {
          data: {
            data: { data },
          },
        } = await AccountantService.fetch(queryString, "");

        if (!data || data.length === 0) {
          cb([this.makeSentinel(SENTINEL_EMPTY)]);
          return;
        }

        const result = [...data].map((item) => ({
          ...item,
          value: item.ma_tai_khoan,
          is_sentinel: false,
        }));

        cb(result);
      } catch (error) {
        console.log(error);
        cb([this.makeSentinel(SENTINEL_EMPTY)]);
      } finally {
        this.$emit("on-loading", false);
      }
    },

    async queryThongTinChiTiet(queryString, cb) {
      if (
        !queryString ||
        queryString.trim() === "" ||
        !this.maTaiKhoanDangChon
      ) {
        cb([]);
        return;
      }

      this.maChiTietBeforeSuggest = this.maChiTietDangChon;
      cb([this.makeSentinel(SENTINEL_LOADING)]);
      this.$emit("on-loading", true);

      try {
        let query = queryString;
        if (this.operator) {
          query += `&operator=${this.operator}`;
        }

        const {
          data: {
            data: { data },
          },
        } = await AccountantService.fetch(
          this.maTaiKhoanDangChon.toString(),
          query
        );

        if (!data || data.length === 0) {
          cb([this.makeSentinel(SENTINEL_EMPTY)]);
          return;
        }

        const result = [...data].map((item) => ({
          ...item,
          value: item.ma_chi_tiet || item.ma_tai_khoan,
          is_sentinel: false,
        }));

        cb(result);
      } catch (error) {
        console.log(error);
        cb([this.makeSentinel(SENTINEL_EMPTY)]);
      } finally {
        this.$emit("on-loading", false);
      }
    },

    onSelectTaiKhoan(value: SuggestionItem) {
      if (this.isSentinelItem(value) || !value.ma_tai_khoan) {
        this.maTaiKhoanDangChon = this.maTaiKhoanBeforeSuggest;
        // Keep chi tiết cleared — do not revive previous ma_chi_tiet
        this.clearChiTietForNewMatkSearch();
        return;
      }

      const phanCap =
        typeof value.phan_cap === "number" ? value.phan_cap : null;
      const allowsChiTiet = phanCap === 0;
      const maTaiKhoan = String(value.ma_tai_khoan);

      this.suppressPropHydrateAfterClear = false;
      this.lastClearedField = null;
      this.maTaiKhoanDangChon = maTaiKhoan;
      this.selectedPhanCap = phanCap;
      this.tenTaiKhoan = value.ten_tai_khoan || "";
      this.maChiTietDangChon = "";
      this.maChiTietBeforeSuggest = "";
      this.suppressMactHydrate = true;

      this.$emit("on-select", {
        taiKhoanItem: {
          ...value,
          // Always clear chi tiết when switching mã TK so parent state stays in sync
          ma_chi_tiet: "",
        },
      });

      nextTick(() => {
        // Re-assert mã after EP writes value-key into the input
        this.maTaiKhoanDangChon = maTaiKhoan;
        if (allowsChiTiet) {
          this.maChiTietInputRef?.focus();
        }
      });
    },

    onSelectTaiKhoanChiTiet(value: SuggestionItem) {
      if (this.isSentinelItem(value) || !value.ma_tai_khoan) {
        this.maChiTietDangChon = this.maChiTietBeforeSuggest || "";
        return;
      }

      const maChiTiet = value.ma_chi_tiet || "";

      this.suppressPropHydrateAfterClear = false;
      this.lastClearedField = null;
      this.maChiTietDangChon = maChiTiet;
      this.tenTaiKhoan = value.ten_tai_khoan || "";
      this.suppressMactHydrate = false;

      this.$emit("on-select", {
        taiKhoanItem: value,
      });

      nextTick(() => {
        this.maChiTietDangChon = maChiTiet;
      });
    },
  },
});
